/* eslint-disable react-hooks/rules-of-hooks */
import PriceRowLoyaltyPoints from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowLoyaltyPointsLoadingSkeleton from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPointsLoadingSkeleton'
import PriceRowLoyaltyPointsPromoPill from 'components/Luxkit/PricePoints/Loyalty/PriceRowLoyaltyPointsPromoPill'
import { useAppSelector } from 'hooks/reduxHooks'
import { nonNullable } from 'lib/array/arrayUtils'
import { LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL } from 'loyalty/constants/loyaltyBonusPoints'
import useLoyaltyPointsEarnCalculator from 'loyalty/hooks/useLoyaltyPointsEarnCalculator'
import determineLoyaltyBonusPointsCampaignType, { determineOfferLoyaltyBonusPointsCampaignType } from 'loyalty/lib/determineLoyaltyBonusPointsCampaignType'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import React, { ComponentProps, useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

type InheritedPriceRowLoyaltyPointsProps = Pick<ComponentProps<typeof PriceRowLoyaltyPoints>, 'onInfoIconClick' | 'calculationType' | 'horizontalAlign'>

interface Props extends InheritedPriceRowLoyaltyPointsProps {
  calculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined> | App.LoyaltyPointsEarnCalculationRequest | undefined;
  /**
   * @default 'S'
   */
  size?: React.ComponentProps<typeof PriceRowLoyaltyPoints>['size'];
  /**
   * @default false
   */
  hideStatusCredits?: boolean;
  offer?: App.Offer | App.HotelOffer | App.HotelOfferSummary | App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
}

function _LoyaltyPointsEarn(props: Props) {
  const {
    calculationRequests,
    size = 'S',
    hideStatusCredits = false,
    calculationType,
    horizontalAlign,
    offer,
    onInfoIconClick,
  } = props

  const loyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const displayTier = useAppSelector(getLoyaltyDisplayTier)

  const [loyaltyPointsRef, loyaltyPointsInView] = useInView({
    triggerOnce: true,
    rootMargin: '400px 0px 400px 0px',
  })

  const { totalPointsCalculation } = useLoyaltyPointsEarnCalculator(calculationRequests, {
    skipAll: !loyaltyPointsInView,
  })

  const initial = totalPointsCalculation?.initial
  const fetching = !!totalPointsCalculation?.fetching
  const err = totalPointsCalculation?.error
  const points = totalPointsCalculation?.data?.points ?? 0
  const campaignTypeFromCalculation = totalPointsCalculation?.data ? determineLoyaltyBonusPointsCampaignType(totalPointsCalculation.data) : undefined
  const bonusPointsCampaignType = useMemo(() => {
    if (campaignTypeFromCalculation) return campaignTypeFromCalculation
    if (calculationType === 'estimate' && offer) {
      return determineOfferLoyaltyBonusPointsCampaignType(offer)
    }
  }, [campaignTypeFromCalculation, calculationType, offer])
  const trailingCaption = useMemo<string | undefined>(() => {
    if (!campaignTypeFromCalculation && calculationType === 'estimate' && offer) {
      const campaignTypeFromOffer = determineOfferLoyaltyBonusPointsCampaignType(offer)
      if (campaignTypeFromOffer) {
        switch (offer.type) {
          case 'hotel':
          case 'tactical_ao_hotel':
            return '(select packages)'
          case 'tour':
          case 'direct_tour':
          case 'partner_tour':
          case 'connection_tour':
            return '(select departures)'
        }
      }
    }
  }, [calculationType, offer, campaignTypeFromCalculation])
  const statusCredits = totalPointsCalculation?.data?.statusCredits ?? 0

  if (
    !loyaltyEnabled ||
    !calculationRequests ||
    (Array.isArray(calculationRequests) && nonNullable(calculationRequests).length === 0) ||
    err ||
    (!initial && !fetching && !points)) return null

  return <div ref={loyaltyPointsRef}>
    {fetching && <PriceRowLoyaltyPointsLoadingSkeleton tier={displayTier} size={size} />}
    {!fetching && <>
      {!bonusPointsCampaignType && <PriceRowLoyaltyPoints
        tier={displayTier}
        earnablePoints={points}
        size={size}
        calculationType={calculationType}
        statusCredits={hideStatusCredits ? undefined : statusCredits}
        horizontalAlign={horizontalAlign}
        onInfoIconClick={onInfoIconClick}
      />}
      {!!bonusPointsCampaignType && <PriceRowLoyaltyPointsPromoPill
        promo={LOYALTY_BONUS_POINTS_CAMPAIGN_LABEL[bonusPointsCampaignType]}
        tier={displayTier}
        earnablePoints={points}
        size={size}
        calculationType={calculationType}
        statusCredits={hideStatusCredits ? undefined : statusCredits}
        horizontalAlign={horizontalAlign}
        trailingCaption={trailingCaption}
        onPillClick={onInfoIconClick}
      />}
    </>}
  </div>
}

export default _LoyaltyPointsEarn
